import "./ResTecnicoPage.css";

// import hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { ResTecnicoAccess } from "../../components/RoleBasedAccess";
import NavCard from "../../components/NavCard";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import { useState, useEffect, useMemo } from "react";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { differenceInDays, format } from "date-fns";

const settoriLegacy = [
  { value: "Edile", label: "Settore Edile" },
  { value: "Cucine", label: "Settore Equipment Cucine" },
  { value: "Intesa", label: "Settore Banca Intesa" },
  { value: "Elettrico", label: "Settore Elettrico" },
  { value: "FTV", label: "Settore Fotovoltaico" },
  { value: "ROBY", label: "Personale Roby" },
  { value: "Amministrazione", label: "Amministrazione" },
  { value: "Cessati", label: "Ex Dipendenti" },
];

export default function ResTecnicoPage() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);
  const { document: userProfile } = useDocument("users", user?.uid);
  const { documents: automezzi } = useCollection("automezzi");
  const { documents: settoriDocs } = useCollection("settori");
  const { documents: allUsers } = useCollection("users");
  const { documents: allFerie } = useCollection("ferie");
  const { documents: richiesteInfoDocs } = useCollection("richiesteInfo");
  const { documents: ticketsDocs } = useCollection("tickets");
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (userProfile && automezzi) {
      const userSectorCode = userProfile.anagrafica?.settore;
      // Se l'utente non ha un settore assegnato, non mostriamo nulla (o gestiamo diversamente)
      if (!userSectorCode) return;

      // Mappatura settore utente (codice) -> label settore
      const legacyObj = settoriLegacy.find((s) => s.value === userSectorCode);
      const userSectorLabel = legacyObj ? legacyObj.label : userSectorCode;

      // Cerchiamo anche l'ID del settore nella collezione 'settori' se disponibile
      let firestoreSectorId = null;
      if (settoriDocs) {
        const firestoreSector = settoriDocs.find(
          (s) => s.nome === userSectorLabel || s.nome === userSectorCode,
        );
        if (firestoreSector) {
          firestoreSectorId = firestoreSector.id;
        }
      }

      // Filtra automezzi del settore
      let myVehicles = [];

      // ECCEZIONE: Se il settore è "ROBY", mostra tutti i veicoli senza filtri
      if (userSectorCode === "ROBY") {
        myVehicles = automezzi;
      } else {
        // Altrimenti applica il filtro per settore
        myVehicles = automezzi.filter((v) => {
          // 1. Corrispondenza esatta ID Firestore (più affidabile)
          if (firestoreSectorId && v.settoreId === firestoreSectorId)
            return true;
          // 2. Corrispondenza Nome/Label (es. "Settore Edile")
          if (v.settoreNome === userSectorLabel) return true;
          // 3. Corrispondenza Codice (es. "Edile") - fallback se i nomi non coincidono perfettamente
          if (v.settoreNome === userSectorCode) return true;
          // 4. Corrispondenza ID diretto (caso legacy o coincidenza)
          if (v.settoreId === userSectorCode) return true;

          return false;
        });
      }

      const newAlerts = [];
      const today = new Date();

      // Helper per ottenere il nome del settore
      const getSectorName = (v) => {
        if (v.settoreNome) return v.settoreNome;
        if (v.settoreId && settoriDocs) {
          const s = settoriDocs.find((doc) => doc.id === v.settoreId);
          if (s) return s.nome;
        }
        return "N/D";
      };

      myVehicles.forEach((v) => {
        // Check Revisione (avviso 60 giorni prima)
        if (v.scadenzaRevisione) {
          const scadenza = new Date(v.scadenzaRevisione);
          const diff = differenceInDays(scadenza, today);
          // Avvisa se scaduta o scade entro 60 giorni
          if (diff <= 60) {
            newAlerts.push({
              id: v.id + "_rev",
              type: "Revisione",
              targa: v.targa,
              modello: v.modello,
              settore: getSectorName(v),
              scadenza: v.scadenzaRevisione,
              days: diff,
              isExpired: diff < 0,
            });
          }
        }
        // Check Assicurazione (avviso 5 giorni prima)
        if (v.scadenzaAssicurazione) {
          const scadenza = new Date(v.scadenzaAssicurazione);
          const diff = differenceInDays(scadenza, today);
          // Avvisa se scaduta o scade entro 5 giorni
          if (diff <= 5) {
            newAlerts.push({
              id: v.id + "_ass",
              type: "Assicurazione",
              targa: v.targa,
              modello: v.modello,
              settore: getSectorName(v),
              scadenza: v.scadenzaAssicurazione,
              days: diff,
              isExpired: diff < 0,
            });
          }
        }
      });

      // Ordina per urgenza (scaduti prima, poi i più vicini)
      newAlerts.sort((a, b) => a.days - b.days);
      setAlerts(newAlerts);
    }
  }, [userProfile, automezzi, settoriDocs]);

  const pendingFerieCount = useMemo(() => {
    if (!allUsers || !allFerie) return 0;

    const pendingFerie = allFerie.filter((req) => req.status === "pending");

    if (pendingFerie.length === 0) return 0;

    let mySector = userProfile?.anagrafica?.settore;

    if (!mySector && allUsers) {
      const me = allUsers.find((u) => u.id === user.uid);
      mySector = me?.anagrafica?.settore;
    }

    if (typeof mySector === "string") {
      mySector = mySector.trim();
    }

    const isSuperSector = mySector === "ROBY";
    const isAdminNoSector = admin && !mySector;

    if (isSuperSector || isAdminNoSector) {
      return pendingFerie.length;
    }

    if (!mySector) return 0;

    const sectorUserIds = new Set();
    allUsers.forEach((u) => {
      let uSector = u.anagrafica?.settore;
      if (typeof uSector === "string") {
        uSector = uSector.trim();
      }
      if (uSector === mySector) {
        sectorUserIds.add(u.id);
      }
    });

    const count = pendingFerie.filter((req) =>
      sectorUserIds.has(req.userId),
    ).length;

    return count;
  }, [allUsers, allFerie, user.uid, userProfile, admin]);

  const pendingRichiesteInfoCount = useMemo(() => {
    if (!richiesteInfoDocs) return 0;

    let mySector = userProfile?.anagrafica?.settore;

    if (!mySector && allUsers) {
      const me = allUsers.find((u) => u.id === user.uid);
      mySector = me?.anagrafica?.settore;
    }

    if (typeof mySector === "string") {
      mySector = mySector.trim();
    }

    const isSuperSector = mySector === "ROBY";
    const isAdminNoSector = admin && !mySector;

    if (isSuperSector || isAdminNoSector) {
      return richiesteInfoDocs.filter((r) => {
        if (!r.settori || !Array.isArray(r.settori)) return false;
        return r.settori.some((s) => s.status === "pending");
      }).length;
    }

    if (!mySector) return 0;

    const mySectorNorm = mySector;

    const matchesSectorName = (nomeSettore) => {
      if (!nomeSettore) return false;
      let nome = nomeSettore;
      if (typeof nome === "string") {
        nome = nome.trim();
      }
      if (nome === mySectorNorm) return true;
      if (nome.includes(mySectorNorm)) return true;
      if (mySectorNorm.includes(nome)) return true;
      return false;
    };

    return richiesteInfoDocs.filter((r) => {
      if (!r.settori || !Array.isArray(r.settori)) return false;
      return r.settori.some(
        (s) => s.status === "pending" && matchesSectorName(s.nome),
      );
    }).length;
  }, [richiesteInfoDocs, allUsers, user.uid, userProfile, admin]);

  const pendingTicketsTalosCount = useMemo(() => {
    if (!ticketsDocs) return 0;

    const assegnati = ticketsDocs.filter((t) => t.status === "assegnato");
    if (assegnati.length === 0) return 0;

    let mySector = userProfile?.anagrafica?.settore;
    if (!mySector && allUsers) {
      const me = allUsers.find((u) => u.id === user.uid);
      mySector = me?.anagrafica?.settore;
    }
    if (typeof mySector === "string") {
      mySector = mySector.trim();
    }

    const isSuperSector = mySector === "ROBY";
    const isAdminNoSector = admin && !mySector;

    const userSectorCode = mySector;
    const legacyObj = settoriLegacy.find((s) => s.value === userSectorCode);
    const userSectorLabel = legacyObj ? legacyObj.label : userSectorCode;

    let firestoreSectorId = null;
    if (settoriDocs) {
      const firestoreSector = settoriDocs.find(
        (s) => s.nome === userSectorLabel || s.nome === userSectorCode,
      );
      if (firestoreSector) {
        firestoreSectorId = firestoreSector.id;
      }
    }

    const filteredBySector = assegnati.filter((t) => {
      if (isSuperSector || isAdminNoSector) return true;
      if (!mySector) return false;

      if (
        firestoreSectorId &&
        (t.settoreId === firestoreSectorId ||
          t.settore?.id === firestoreSectorId)
      )
        return true;
      if (
        t.settoreNome === userSectorLabel ||
        t.settore?.nome === userSectorLabel
      )
        return true;
      if (
        t.settoreNome === userSectorCode ||
        t.settore?.nome === userSectorCode
      )
        return true;
      if (t.settoreId === userSectorCode || t.settore?.id === userSectorCode)
        return true;

      return false;
    });

    const count = filteredBySector.filter((t) => {
      if (!t.vistiRT || !Array.isArray(t.vistiRT)) return true;
      return !t.vistiRT.some((v) => v.userId === user.uid);
    }).length;

    return count;
  }, [ticketsDocs, allUsers, user.uid, userProfile, admin, settoriDocs]);

  // Definizione delle card per la navigazione
  const elementiResTecnico = [
    {
      action: "projects",
      nome: "Gestione Commesse e Cantieri",
      message: "Visualizza e gestisci i commesse e cantieri",
      tipo: "cantieri",
    },
    {
      action: "presenze-tecnico",
      nome: "Monitoraggio Presenze",
      message: "Controlla le presenze dei dipendenti del tuo settore",
      tipo: "presenze",
    },
    {
      action: "dashboard-reperibilita",
      nome: "Dashboard Reperibilità",
      message:
        "Visualizza i turni di reperibilità del tuo settore, filtrati per mese",
      tipo: "presenze",
    },
    {
      action: "pianificazione-tecnico",
      nome: "Pianificazione Attività",
      message: "Pianifica le attività giornaliere del team",
      tipo: "presenze",
    },
    {
      action: "tabella-manutenzioni",
      nome: "Tabella Manutenzioni",
      message:
        "Consulta le manutenzioni da contratto e applica offset alle attività pianificate",
      tipo: "presenze",
    },
    {
      action: "gestione-ferie-tecnico",
      nome: "Gestione Ferie",
      message: "Approva o rifiuta le richieste ferie del team",
      tipo: "presenze",
      notificationCount: pendingFerieCount,
    },
    {
      action: "richieste-info-tecnico",
      nome: "Richieste Info Documenti",
      message:
        "Visualizza le richieste dell'amministrazione e associa le fatture ai cantieri",
      tipo: "presenze",
      secondaryNotificationCount: pendingRichiesteInfoCount,
    },
    {
      action: "tickets-tecnico",
      nome: "Ticket Talos",
      message:
        "Visualizza i ticket Talos assegnati al tuo settore e marca gli interventi eseguiti",
      tipo: "talos",
      orangeNotificationCount: pendingTicketsTalosCount,
    },
  ];

  return (
    <ResTecnicoAccess>
      <div className="restecnico-container">
        <h1>Area Responsabile Tecnico</h1>
        <div className="restecnico-content">
          <div className="welcome-section">
            <h2>Benvenuto, {user.displayName}</h2>
            <p>
              Questa pagina è accessibile solo ai Responsabili Tecnici e agli
              Amministratori.
            </p>
          </div>

          {alerts.length > 0 && (
            <div className="alerts-section">
              <h3>
                <span className="warning-icon">⚠️</span> Scadenze Automezzi
              </h3>
              <div className="alerts-list">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`alert-item ${
                      alert.isExpired ? "expired" : "upcoming"
                    }`}
                  >
                    <div>
                      <div className="alert-header">
                        {alert.type}: {alert.modello} ({alert.targa} -{" "}
                        {alert.settore})
                      </div>
                      <div className="alert-details">
                        Scadenza:{" "}
                        {format(new Date(alert.scadenza), "dd/MM/yyyy")}
                      </div>
                    </div>
                    <div className="days-left">
                      {alert.isExpired
                        ? `Scaduta da ${Math.abs(alert.days)} giorni`
                        : `Scade tra ${alert.days} giorni`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="info-section">
            <h3>Funzionalità disponibili</h3>
            <div className="section-cards">
              {elementiResTecnico.map((elem) => (
                <NavCard key={elem.nome} data={elem} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </ResTecnicoAccess>
  );
}
